// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.FieldNameTab.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * FieldNameTab component
 */
import React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import Panel from 'share/components/controls/Izenda.Panel';
import Label from 'share/components/controls/Izenda.Label';
import DropDown from 'share/components/controls/Izenda.DropDown';
import FormGroup from 'share/components/controls/Izenda.FormGroup';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import CalculatedFieldConstants from 'logic/data/Izenda.CalculatedFieldConstants';
import 'share/utils/Izenda.StringUtilities';

const constants = CalculatedFieldConstants.FIELD_NAME;
const TABLE_DEF = CalculatedFieldConstants.TABLE_DEFINITION.FieldName;

@observer
class FieldNameTab extends React.Component {
  static displayName = 'FieldNameTab';

  static propTypes = {
    store: React.PropTypes.object.isRequired
  };

  static defaultProps = {
    displayDataSource: true
  };

  render() {
    let store = this.props.store;

    return (
      <div className="form-horizontal expression-tab">
        <Label className="col-md-12 instruction">
          {constants.instruction}
        </Label>

        {this.renderDataSourceDropdown()}

        <Table
          dataSource={store.fieldNames}
          onSortChange={this.onSortChange}
          sortInfo={store.pageInfo.colSortDirs}>
          {/* Field name */}
          <Column {...TABLE_DEF['fieldName']} cell={this.renderFieldName} />

          {/* Data type */}
          <Column {...TABLE_DEF['dataType']} cell={this.renderDataType} />

          {/* Description */}
          <Column {...TABLE_DEF['description']} cell={this.renderDescription} />
        </Table>
      </div>
    );
  }

  /**
   * Render data source drop-down
   */
  renderDataSourceDropdown() {
    let { store, displayDataSource, expressionConfig } = this.props;

    return displayDataSource
      ? <div>
          <Label className="col-md-3 control-label data-source-label">
            {constants.dataSourceLabel}
          </Label>
          <FormGroup className="col-md-9 data-source-container">
            <DropDown
              className="data-source"
              onChange={this.onDataSourceChange}
              options={store.dataSources}
              value={store.selectedDataSource && store.selectedDataSource.id}
              valueField="id"
              labelField="name"
              disabled={!expressionConfig.enableDataSource}
              isOnPopover
            />
          </FormGroup>
        </div>
      : null;
  }

  /**
   * Render field name column
   * @param {Object} detail
   */
  renderFieldName = (detail: Object) => {
    return (
      <span
        className="selectable truncate overflow-text"
        title={detail.name}
        onMouseDown={this.onSelect.bind(this, detail)}>
        {detail.name}
      </span>
    );
  };

  /**
   * Render data type column
   * @param {Object} detail
   */
  renderDataType = (detail: Object) => {
    return (
      <span onMouseDown={this.onSelect.bind(this, detail)}>
        {detail.dataType}
      </span>
    );
  };

  /**
   * Render description column
   * @param {Object} detail
   */
  renderDescription = (detail: Object) => {
    return (
      <span onMouseDown={this.onSelect.bind(this, detail)}>
        {detail.description}
      </span>
    );
  };

  /**
   * Handle on sort change.
   * @param {String} columnKey
   * @param {String} sortDir
   */
  onSortChange = (columnKey: String, sortDir: String) => {
    this.props.store.pageInfo.setColSortDirs(columnKey, sortDir);
    this.props.store.sort();
  };

  /**
   * Handle on select a field name.
   * @param {Object} detail 
   */
  onSelect(detail) {
    let {
      store,
      store: { selectedDataSource },
      expressionConfig,
      displayDataSource,
      onSelect
    } = this.props;

    let selectedName = String.format(
      expressionConfig.returnedOnlyFieldNameFormat,
      detail.name
    );
    let sourceInfo = displayDataSource ? selectedDataSource : detail;
    sourceInfo.dataSourceName = displayDataSource
      ? selectedDataSource.name
      : detail.querySourceName;

    if (!expressionConfig.subOrGrandTotal && sourceInfo.databaseName) {
      selectedName = String.format(
        expressionConfig.returnedFieldNameFormat,
        sourceInfo.databaseName,
        sourceInfo.schemaName,
        sourceInfo.dataSourceName,
        detail.name
      );
    }

    onSelect && onSelect(selectedName);
  }

  /**
   * Handle on change of data source
   * @param {Object} e
   */
  onDataSourceChange = (e: Object) => {
    let store = this.props.store;
    let item = store.dataSources.find(item => item.id === e.value);
    store.updateSelectedDataSource(item);
    item && store.fetchFieldNames(item.fieldNames);
  };
}

export default FieldNameTab;



// WEBPACK FOOTER //
// ./ui/share/components/controls/calculatedField/Izenda.FieldNameTab.js